import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Headphones, PhoneOff, Users } from "lucide-react";
import type { AuthUser } from "../../auth/auth";
import { cn } from "../../lib/utils";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import type { QualitySeverity } from "../../room/connection-quality";
import { AccountControl } from "../auth/AccountControl";
import { CameraControl } from "../media/CameraControl";
import { MicrophoneControl } from "../media/MicrophoneControl";
import { QualityIcon, qualityColor } from "../media/QualityIcon";
import { ScreenControl } from "../media/ScreenControl";
import { Button } from "../ui/button";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Separator } from "../ui/separator";
import { ConnectionDetailsBody, useQualitySections } from "./ConnectionDetailsBody";
import { DockCardContext, dockPopoverProps, useDockCard } from "./dock-anchor";

// Same meta text as RoomCard.
const roomMeta = "text-xs leading-4.5 font-normal text-muted-foreground";
// Status-tinted surface for the icon-only connection button.
const qualityTint: Record<QualitySeverity, string> = {
  info: "bg-muted-foreground/15 hover:ring-muted-foreground/25 data-[state=open]:ring-muted-foreground/25",
  good: "bg-quality-good/15 hover:ring-quality-good/25 data-[state=open]:ring-quality-good/25",
  warning:
    "bg-quality-warning/15 hover:ring-quality-warning/25 data-[state=open]:ring-quality-warning/25",
  critical:
    "bg-quality-critical/15 hover:ring-quality-critical/25 data-[state=open]:ring-quality-critical/25",
};
const roundIconButton = "grid size-8 flex-[0_0_2rem] place-items-center rounded-full p-0";
// Same hover as the media pills: an overlay instead of the variant's fill change.
const pillHover =
  "relative after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:content-[''] hover:after:bg-white/[.11] [&>svg]:relative [&>svg]:z-1";

function ConnectionStatus({ away }: { away: boolean }) {
  const { t } = useTranslation();
  const controller = useRoomController();
  const open = useRoomState((s) => s.quality.hostQualityOpen);
  const severity = useRoomState((s) => s.quality.hostQualitySeverity);
  const label = useRoomState((s) => s.quality.hostQualityLabel) || t("connection.state.offline");
  const details = useRoomState((s) => s.quality.hostQualityDetails);
  // Away from the room the user is browsing; only a change in severity is worth
  // interrupting them for.
  const [announced, setAnnounced] = useState({ severity, label });
  if (announced.severity !== severity) setAnnounced({ severity, label });
  const section = useQualitySections();
  const card = useDockCard();
  const name = `${label} — ${t("connection.serverConnection")}`;
  return (
    <Popover open={open} onOpenChange={(value) => controller.setQualityPopoverOpen("host", value)}>
      {card ? <PopoverAnchor virtualRef={card} /> : null}
      <PopoverTrigger asChild>
        <Button
          id="hostQualityAction"
          variant={null}
          data-quality={severity}
          aria-label={name}
          title={name}
          className={cn(
            roundIconButton,
            "transition-shadow hover:ring-4 data-[state=open]:ring-4",
            qualityTint[severity],
            qualityColor[severity],
          )}
        >
          <QualityIcon severity={severity} className="size-4" />
        </Button>
      </PopoverTrigger>
      {/* The button's name already carries the label; this keeps changes announced. */}
      <span id="hostQualityLabel" role="status" className="sr-only">
        {away ? announced.label : label}
      </span>
      <PopoverContent
        id="hostQualityPopover"
        aria-labelledby="hostQualityTitle"
        aria-describedby="hostQualityDescription"
        {...dockPopoverProps}
        collisionPadding={8}
        className="max-h-[calc(100dvh-10rem)] w-90 max-w-[calc(100vw-1rem)] overflow-y-auto"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <ConnectionDetailsBody kind="host" details={details} {...section} />
      </PopoverContent>
    </Popover>
  );
}

function RoomMeta({
  hasPasscode,
  maxParticipants,
}: {
  hasPasscode: boolean;
  maxParticipants?: number;
}) {
  const { t } = useTranslation();
  const count = useRoomState((s) => s.participants.peopleCount);
  const people = maxParticipants
    ? t("home.occupancyLabel", { count, max: maxParticipants })
    : t("common.person", { count });
  return (
    <p
      className={cn("flex min-w-0 items-center gap-2 overflow-hidden whitespace-nowrap", roomMeta)}
    >
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate">{t(hasPasscode ? "home.passcodeRequired" : "home.noCode")}</span>
      </span>
      <span
        className={cn(
          "flex flex-none items-center gap-1 tabular-nums before:mr-1 before:content-['·']",
          roomMeta,
        )}
        title={people}
        aria-label={people}
      >
        <Users className="size-3" aria-hidden="true" />
        {maxParticipants ? `${count}/${maxParticipants}` : count}
      </span>
    </p>
  );
}

// The one call dock: in the room, and (with onReturn) away from it.
export function RoomDock({
  roomName,
  hasPasscode,
  maxParticipants,
  user,
  onSignOut,
  onReturn,
  className,
}: {
  roomName: string;
  hasPasscode: boolean;
  maxParticipants?: number;
  user: AuthUser | null;
  onSignOut: () => void;
  onReturn?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const { leave, toggleParticipantVoices } = useRoomController();
  const active = useRoomState((s) => s.participants.participantVoicesActive);
  const muted = useRoomState((s) => s.participants.otherVoicesMuted);
  const voiceLabel = t(muted ? "voice.unmuteOtherVoices" : "voice.muteOtherVoices");
  const card = useRef<HTMLDivElement>(null);
  return (
    <DockCardContext value={card}>
      <div
        ref={card}
        role="group"
        aria-label={t("stage.roomControls")}
        // The media row sets the width; only the top row (name, meta) gives way.
        className={cn("grid w-fit gap-2 rounded-3xl bg-card p-2 shadow-md", className)}
      >
        {/* w-0 + min-w-full: fills the media row's width without widening the card.
            h-9 keeps the row a whole 36px (name + meta lines) so the dock height never lands on a half pixel. */}
        <div className="flex h-9 w-0 min-w-full items-center gap-2.5">
          <ConnectionStatus away={Boolean(onReturn)} />
          <div className="grid min-w-0 flex-1 justify-items-start">
            <strong className="w-full truncate text-sm leading-4.5 font-semibold" title={roomName}>
              {roomName}
            </strong>
            <RoomMeta hasPasscode={hasPasscode} maxParticipants={maxParticipants} />
          </div>
          {onReturn ? (
            <Button
              variant="secondary"
              size="icon"
              className={cn(roundIconButton, pillHover, "hover:bg-secondary")}
              type="button"
              aria-label={t("callBar.return")}
              title={t("callBar.return")}
              onClick={onReturn}
            >
              <ArrowRight size={18} />
            </Button>
          ) : null}
          <Button
            variant="destructive"
            size="icon"
            className={cn(roundIconButton, pillHover, "hover:bg-destructive-fill")}
            type="button"
            aria-label={t("home.leave")}
            title={t("home.leave")}
            onClick={() => void leave()}
          >
            <PhoneOff size={18} />
          </Button>
        </div>
        <Separator />
        <div
          id="mediaControlCluster"
          className="screen-share-pill media-control-cluster relative z-2 flex items-center justify-center gap-2 [--dock-control-size:calc(var(--spacing)*8)] [&_button>svg]:relative [&_button>svg]:z-1 [&_button>svg]:block"
        >
          <AccountControl user={user} onSignOut={onSignOut} />
          <ScreenControl />
          <CameraControl />
          <MicrophoneControl />
          <Button
            id="toggleParticipantVoices"
            data-active={active}
            variant="secondary"
            size="icon-sm"
            className={cn(
              "dock-button participant-voices-toggle shrink-0 rounded-full data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:hover:bg-primary",
              pillHover,
              "hover:bg-secondary",
            )}
            type="button"
            aria-label={voiceLabel}
            aria-pressed={muted}
            title={voiceLabel}
            onClick={toggleParticipantVoices}
          >
            <Headphones aria-hidden="true" />
          </Button>
        </div>
      </div>
    </DockCardContext>
  );
}
